import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../../shared/toastr.service";
import { Signature } from "../../signatures.interface";
import { SignaturesService } from "../../signatures.service";

@Component({
  selector: "app-signature-domain-rules",
  templateUrl: "./domain-rules.component.html",
  styleUrls: ["./domain-rules.component.scss"],
  standalone: false,
})
export class DomainRulesComponent implements OnChanges, OnInit, OnDestroy {
  private static readonly INSTALL_ONLY_MODE = "INSTALL_ANY";

  @Input() footer: Signature | null = null;
  @Input() disabled = false;
  @Output() changed = new EventEmitter<void>();

  saving = false;
  private modeSubscription?: Subscription;

  form = new UntypedFormGroup({
    domainRules: new UntypedFormControl(""),
    domainRulesMatchMode: new UntypedFormControl("ALL_RECIPIENTS"),
    domainRulesIgnoreSenderDomain: new UntypedFormControl(false),
  });

  constructor(
    private readonly service: SignaturesService,
    private readonly toast: ToastrService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.modeSubscription =
      this.form.controls.domainRulesMatchMode.valueChanges.subscribe((mode) => {
        this.syncInstallOnlyState(mode);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["footer"]?.currentValue) {
      const footer = changes["footer"].currentValue as Signature;
      this.form.patchValue(
        {
          domainRules: footer.domainRules?.join(", ") ?? "",
          domainRulesMatchMode: footer.domainRulesMatchMode ?? "ALL_RECIPIENTS",
          domainRulesIgnoreSenderDomain:
            footer.domainRulesIgnoreSenderDomain === true,
        },
        { emitEvent: false },
      );
      this.syncInstallOnlyState(this.form.controls.domainRulesMatchMode.value);
      this.form.markAsPristine();
    }
  }

  get isInstallOnlyMode(): boolean {
    return (
      this.form.controls.domainRulesMatchMode.value ===
      DomainRulesComponent.INSTALL_ONLY_MODE
    );
  }

  ngOnDestroy(): void {
    this.modeSubscription?.unsubscribe();
  }

  private syncInstallOnlyState(mode: unknown): void {
    const isInstallOnlyMode = mode === DomainRulesComponent.INSTALL_ONLY_MODE;
    const domainRulesControl = this.form.controls.domainRules;
    const ignoreSenderControl =
      this.form.controls.domainRulesIgnoreSenderDomain;

    if (isInstallOnlyMode) {
      this.form.patchValue({
        domainRules: "",
        domainRulesIgnoreSenderDomain: false,
      });
      domainRulesControl.disable({ emitEvent: false });
      ignoreSenderControl.disable({ emitEvent: false });
      return;
    }

    domainRulesControl.enable({ emitEvent: false });
    ignoreSenderControl.enable({ emitEvent: false });
  }

  save(): void {
    if (!this.footer?.templateId || this.saving || !this.form.dirty) {
      return;
    }

    this.saving = true;
    const formValue = this.form.getRawValue() as {
      domainRules: string;
      domainRulesMatchMode: "ANY_RECIPIENT" | "ALL_RECIPIENTS" | "INSTALL_ANY";
      domainRulesIgnoreSenderDomain: boolean;
    };
    const isInstallOnlyMode =
      formValue.domainRulesMatchMode === DomainRulesComponent.INSTALL_ONLY_MODE;

    const payload: Record<string, unknown> = {
      ...this.footer,
      domainRules: isInstallOnlyMode ? "" : formValue.domainRules,
      domainRulesMatchMode: formValue.domainRulesMatchMode,
      domainRulesIgnoreSenderDomain: isInstallOnlyMode
        ? false
        : formValue.domainRulesIgnoreSenderDomain,
    };

    this.service.installDomainRules(this.footer.templateId, payload).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant(
            "footers.domainRulesInstalledSuccess",
          ) as string,
        );
        this.form.markAsPristine();
        this.changed.emit();
        this.saving = false;
      },
      error: () => {
        this.toast.error(
          this.translate.instant("footers.domainRulesInstalledError") as string,
        );
        this.saving = false;
      },
    });
  }
}
